import React, { useEffect, useMemo, useState, useCallback } from "react";
import ToolbarHeader from "../../components/ToolbarHeader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomAccordion from "../../components/CustomAccordion";
import { useAuth } from "../../contexts/AuthContext";
import {
  adminColumns,
  areaManagerColumns,
  areaManagerNestedSubheadColumns,
  areaManagerSubheadColumns,
} from "../../components/Columns/VoucherDetails";
import useApi from "../../hooks/useApi";
import { apiUrls } from "../../utils/api/apiUrls";
import { toast } from "react-toastify";
import DetailsCard from "../../components/DetailsCard";
import PreviewDocModal from "../cashDeposits/PreviewDocModal";
import { FaChevronRight } from "react-icons/fa";
import RejectModal from "../../CustomRejectModal/RejectModal";
import { icons } from "../../assets/icons";
import PreviewModal from "./PreviewModal";
import { useMediaQuery } from "react-responsive";
import EditApproveModal from "../../CustomEditApproveModal/EditApproveModal";
import { approvedAmountPassAccordingToLevel, canSendBackForRework, hasAnyChildInRework } from "../../utils/helpers";
import EditVoucherComment from "../../CustomEditVoucherCommentModal/EditVoucherComment";
import SendBackForReworkModal from "../../CustomSendBackForReworkModal/SendBackForReworkModal";
import EditAfterReworkModal from "../../CustomEditAfterReworkModal/EditAfterReworkModal";
import ReworkHistoryModal from "../../CustomReworkHistoryModal/ReworkHistoryModal";
import CustomButton from "../../components/CustomButton";
import {
  collectVoucherDetailLineItems,
  mapChildDocumentRow,
} from "../../../srcWeb/utils/dmsStandaloneListUtils";

const getAttr = (doc, name) =>
  doc?.dataModalAttributeSet?.find((attr) => attr.metadataTechnicalName === name)?.attributeValue;

const sanitizeActionComment = (comment) => {
  if (!comment) return "";
  const trimmed = String(comment).trim();
  if (!trimmed) return "";
  const lower = trimmed.toLowerCase();
  if (lower === "bulk send all - approved") return "Approved";
  if (lower === "bulk send all - rejected") return "Rejected";
  if (lower === "resubmitted after rework") return "";
  return comment;
};

/**
 * Store list rows may be a thin document slice; detail needs getRecordById payload.
 * @param {{ reworkListNavigation?: boolean }} [opts] - Rework tab list rows: `documents[]` alone is not enough.
 */
function storeVoucherStateHasRenderableDocs(s, opts = {}) {
  const { reworkListNavigation = false } = opts;
  if (!s || typeof s !== "object") return false;
  const bucketKeys = [
    "pendingDocs",
    "approvedDocs",
    "rejectedDocs",
    "pendingApprovedRejectedDocs",
    "reworkRequestedDocs",
    "reworkPendingDocs",
  ];
  if (bucketKeys.some((k) => Array.isArray(s[k]) && s[k].length > 0)) {
    return true;
  }
  if (!reworkListNavigation && Array.isArray(s.documents) && s.documents.length > 0) {
    return true;
  }
  return false;
}

const convertDataStructure = (inputData) => {
  let convertedDat = [];
  inputData?.forEach((doc) => {
    convertedDat.push({
      head: getAttr(doc, "head"),
      id: doc?.id,
      filename: doc?.documentName,
      date: getAttr(doc, "date"),
      amount: getAttr(doc, "amount") ?? doc?.amount,
      comment: getAttr(doc, "comment"),
      childId: doc.childId,
      l1_amount_approved: getAttr(doc, "l1_amount_approved") ?? doc?.l1_amount_approved,
      l2_amount_approved: getAttr(doc, "l2_amount_approved") ?? doc?.l2_amount_approved,
      finance_amount_approved: getAttr(doc, "finance_amount_approved") ?? doc?.finance_amount_approved,
      L1: {
        status: doc?.workflowStepResponses?.find(
          (step) => step.userLabel !== "Store"
        )?.status,
      },
      newL1Status: doc?.l1?.status,
      newL2Status: doc?.l2?.status,
      newL3Status: doc?.l3?.status,
      newL1ActionComment: sanitizeActionComment(doc?.l1?.actionComment),
      newL2ActionComment: sanitizeActionComment(doc?.l2?.actionComment),
      newL3ActionComment: sanitizeActionComment(doc?.l3?.actionComment),
      store_code: doc?.storeCode,
      store_name: doc?.storeName,
      reworkStatus: doc?.reworkStatus,
      reworkRequestedByLevel: doc?.reworkRequestedByLevel,
      reworkRequestedAt: doc?.reworkRequestedAt,
      reworkComment: doc?.reworkComment,
      workflowStepResponses: doc?.workflowStepResponses,
      l1: doc?.l1,
      l2: doc?.l2,
      l3: doc?.l3,
      Finance: doc?.Finance,
    });
  });
  return convertedDat;
};

const VoucherDetails = () => {
  const isMobileSmall = useMediaQuery({ query: "(max-width: 336px)" });
  const navigate = useNavigate();
  const { vid } = useParams();
  const { state: fromPrevScreen } = useLocation();

  // Fallback to sessionStorage if state is not available
  const getInitialState = () => {
    if (fromPrevScreen) return fromPrevScreen;
    try {
      const stored = sessionStorage.getItem(`voucher_${vid}_state`);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      // console.error("Error reading from sessionStorage:", e);
    }
    return null;
  };

  const [state, setDataState] = useState(getInitialState());

  useEffect(() => {
    if (fromPrevScreen != null) {
      setDataState(fromPrevScreen);
      return;
    }
    try {
      const stored = sessionStorage.getItem(`voucher_${vid}_state`);
      if (stored) {
        setDataState(JSON.parse(stored));
        return;
      }
    } catch (_) {
      /* ignore */
    }
    setDataState(null);
  }, [vid, fromPrevScreen]);

  const { user: { userLabel } = {} } = useAuth();
  const { apiCall: fileDownload } = useApi();
  const { apiCall: voucherStatusApi } = useApi();
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [downloadPreviewOpenWithData, setDownloadPreviewOpenWithData] =
    useState(false);
  const [isRejectVisible, setIsRejectVisible] = useState({
    isOpen: false,
    data: [],
  });
  const [isEditApproveVisible, setIsEditApproveVisible] = useState({
    isOpen: false,
    data: [],
  });
  const [isEditVoucherCommentVisible, setIsEditVoucherCommentVisible] =
    useState({
      isOpen: false,
      data: {},
    });
  const [isSendBackForReworkVisible, setIsSendBackForReworkVisible] = useState({
    isOpen: false,
    data: [],
  });
  const [isEditAfterReworkVisible, setIsEditAfterReworkVisible] = useState({
    isOpen: false,
    data: {},
  });
  const [isReworkHistoryVisible, setIsReworkHistoryVisible] = useState({
    isOpen: false,
    voucherId: null,
  });
  const [selectedCheckBoxes, setSelectedCheckBoxes] = useState([]);
  const [decisionStatusMap, setDecisionStatusMap] = useState({}); // Track decision status per sub-voucher
  const [canSendAll, setCanSendAll] = useState(false); // Track if all sub-vouchers have decisions
  const [subRow1Data, setSubRow1Data] = useState([]);
  const [subRow2Data, setSubRow2Data] = useState([]);

  useEffect(() => {
    return () => {
      localStorage.removeItem("tempVoucherDetails");
    };
  }, []);

  // Fetch details for non-Store users when needed. Store users use navigation state from getAllRecords when available.
  useEffect(() => {
    if (!vid) return;

    // Store users: fetch when state is missing (reload, direct URL, or after rework submit left state empty)
    if (userLabel === "Store") {
      const source = fromPrevScreen ?? state;
      const reworkEntry = source?.type === "Rework";
      if (
        !source ||
        !storeVoucherStateHasRenderableDocs(source, {
          reworkListNavigation: reworkEntry,
        })
      ) {
        updateVoucherData();
      }
      return;
    }

    const hasDocList = (arr) => Array.isArray(arr) && arr.length > 0;
    const hasRequiredDocs =
      hasDocList(state?.pendingDocs) ||
      hasDocList(state?.approvedDocs) ||
      hasDocList(state?.rejectedDocs) ||
      hasDocList(state?.pendingApprovedRejectedDocs);

    const shouldFetch = !state || !state?.type || !hasRequiredDocs;

    if (shouldFetch) {
      updateVoucherData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vid, state?.type, userLabel, fromPrevScreen]);

  // Update decision status when outputData changes
  // For L2/Finance, canSendAll is driven by backend canSubmitVoucher API (see effect below)
  useEffect(() => {
    if (state && outputData) {
      updateDecisionStatusFromData(state);
      if (userLabel === "L1") {
        checkIfAllDecisionsMade();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, outputData, userLabel]); // Added userLabel to dependencies

  const canSubmitRefreshKey = useMemo(() => {
    if (!state || typeof state !== "object") return "";
    const rowSig = (arr) =>
      Array.isArray(arr) && arr.length
        ? arr
            .map(
              (d) =>
                `${d?.id ?? ""}:${d?.status ?? ""}:${d?.workflowState ?? ""}`
            )
            .sort()
            .join(";")
        : "";
    return [
      String(vid ?? ""),
      state.type,
      state.status,
      rowSig(state.pendingDocs),
      rowSig(state.approvedDocs),
      rowSig(state.rejectedDocs),
      rowSig(state.pendingApprovedRejectedDocs),
    ].join("|");
  }, [vid, state]);

  // L2/Finance: drive Submit All button from backend canSubmitVoucher (single source of truth)
  useEffect(() => {
    const isL2OrFinance = userLabel === "L2" || userLabel === "Finance";
    if (!vid || !isL2OrFinance || !state || (state?.type !== "Pending" && state?.type !== "Rework")) {
      return;
    }
    voucherStatusApi({
      url: `${apiUrls.dmsService.canSubmitVoucher}/${vid}`,
      method: "get",
      onSuccess: (canSubmit) => {
        setCanSendAll(Boolean(canSubmit));
      },
      onError: () => {
        setCanSendAll(false);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vid, userLabel, state?.type, canSubmitRefreshKey]);

  // Choose the best available docs list for the current view.
  // For Pending: use pendingApprovedRejectedDocs when available so we show all sub-vouchers (pending + approved at this level)
  // and Submit enables when all have decisions; otherwise pendingDocs only would hide approved rows.
  const docsForView = useMemo(() => {
    if (!state) return [];
    // Approved view: show only L1-approved/partially-approved items (like Rejected tab shows only rejected)
    if (state?.type === "Approved") {
      const approved = state?.approvedDocs;
      if (approved?.length) return approved;
      const allDocs = state?.pendingApprovedRejectedDocs || state?.pendingDocs || [];
      return allDocs.filter((doc) => {
        const status = doc?.l1?.status ?? doc?.newL1Status ?? doc?.workflowStepResponses?.find((s) => s.userLabel !== "Store")?.status;
        const s = (status || "").toLowerCase();
        return s === "approved" || s === "partially approved" || s === "partiallyapproved" || s === "rework approved" || s === "reworkapproved";
      });
    }
    if (state?.type === "Pending") return state?.pendingApprovedRejectedDocs?.length ? state.pendingApprovedRejectedDocs : (state?.pendingDocs || []);
    if (state?.type === "Rework")
      // Prefer combined/approved docs first to include rework-approved rows
      return (
        state?.pendingApprovedRejectedDocs ||
        state?.approvedDocs ||
        state?.pendingDocs ||
        state?.rejectedDocs ||
        []
      );
    if (state?.type === "All") return state?.pendingApprovedRejectedDocs || [];
    return state?.rejectedDocs || [];
  }, [state]);

  const outputData = useMemo(
    () => (userLabel === "Store" ? state : convertDataStructure(docsForView)),
    [state, userLabel, docsForView]
  );

  // For Store: API (getRecordById) returns pendingDocs, approvedDocs, etc., not "documents".
  // After edit/refresh we set state from API, so state.documents is missing and the table would be empty.
  // Derive combined document list for Store table when documents is not present.
  const storeTableData = useMemo(() => {
    if (userLabel !== "Store" || !outputData) return [];
    const lineItems = collectVoucherDetailLineItems(outputData);
    if (!lineItems.length) return [];
    return lineItems.map((doc) => {
      if (doc?.dataModalAttributeSet?.length) {
        return convertDataStructure([doc])[0];
      }
      return mapChildDocumentRow(doc);
    });
  }, [userLabel, outputData]);

  // Calculate total approved amount from sub-vouchers
  const calculateTotalApprovedAmount = useMemo(() => {
    if (!outputData || !Array.isArray(outputData)) return null;
    
    // Filter out parent rows (rows with subRows) - we only care about actual sub-vouchers
    const actualSubVouchers = outputData.filter((sub) => !sub.subRows || sub.subRows.length === 0);
    
    if (actualSubVouchers.length === 0) return null;
    
    // Calculate sum of approved amounts using priority order
    const totalApproved = actualSubVouchers.reduce((sum, sub) => {
      const approvedAmount = 
        sub?.finance_amount_approved ||
        sub?.l2_amount_approved ||
        sub?.l1_amount_approved ||
        null;
      
      if (approvedAmount) {
        const amount = Number(approvedAmount);
        return sum + (isNaN(amount) ? 0 : amount);
      }
      return sum;
    }, 0);
    
    return totalApproved > 0 ? totalApproved : null;
  }, [outputData]);

  const onClickDownloadFile = useCallback((file_id, filename) => {
    fileDownload({
      url: apiUrls.dmsService.getFile + "/" + file_id,
      method: "get",
      responseType: "blob",
      onSuccess: (apiData) => {
        const url = window.URL.createObjectURL(new Blob([apiData.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      },
    });
  }, [fileDownload]);
  const showStatusColor = (key) => {
    switch (key) {
      case "Pending":
        return "text-[var(--pendingYellow)]";
      case "Approved":
        return "text-[var(--successGreen)]";
      case "Rejected":
        return "text-[var(--textRed)]";
      case "Partially Approved":
        return "text-[var(--pendingYellow)]";
      case "NA":
        return "text-[var(--blackColor)]";
      default:
        return "text-[var(--blackColor)]";
    }
  };

  const Component = ({ data }) => (
    <div>
      <div className="h-[4rem] border-b-[1px] border-[var(--borderGray2)] flex items-center px-5">
        <div className="min-w-[24rem] font-size-small">Status:</div>
        <div className={`font-size-small ${showStatusColor(data?.status)}`}>
          {data?.status}
        </div>
      </div>
      <div className="h-[4rem] border-b-[1px] border-[var(--borderGray2)] flex items-center px-5">
        <div className="min-w-[24rem] font-size-small">Request Amount (₹):</div>
        <div className="font-size-small">
          {(data?.status === "Rejected" || data?.action === "Rejected") ? 0 : data?.amount || 0}
        </div>
      </div>
      <div className="h-[4rem] border-b-[1px] border-[var(--borderGray2)] flex items-center px-5">
        <div className="min-w-[24rem] font-size-small">
          Approved Amount (₹):
        </div>
        <div className="font-size-small">
          {(data?.status === "Rejected" || data?.action === "Rejected") ? 0 : data.approvedAmount || 0}
        </div>
      </div>
      <div className="h-[4rem] border-b-[1px] border-[var(--borderGray2)] flex items-center px-5">
        <div className="min-w-[24rem] font-size-small">Action Date:</div>
        <div className="font-size-small">{data?.actionDate || ""}</div>
      </div>
      <div className="h-[4rem] border-b-[1px] border-[var(--borderGray2)] flex items-center px-5">
        <div className="min-w-[24rem] font-size-small">Action Comment:</div>
        <div className="font-size-small">
          {sanitizeActionComment(data?.actionComment)}
        </div>
      </div>
      <div className="h-[4rem] flex items-center px-5">
        <div className="min-w-[24rem] font-size-small">Action By:</div>
        <div className="font-size-small">{data?.actionBy}</div>
      </div>
    </div>
  );
  // API (Jackson) uses lowercase l1/l2/l3 on the parent voucher; UI historically used L1/L2/L3.
  const parentL1 = outputData?.L1 ?? outputData?.l1;
  const parentL2 = outputData?.L2 ?? outputData?.l2;
  const parentL3 =
    outputData?.L3 ??
    outputData?.l3 ??
    outputData?.Finance ??
    outputData?.finance;

  const accordionItems = [
    {
      isArrowEnable: parentL1 && parentL1?.status !== "Pending",
      renderLeft: () => {
        return (
          <div className={`font-family-medium font-size-small`}>
            L1 - Area Manager
          </div>
        );
      },
      renderRight: () => {
        if (parentL1?.status === "Approved") {
          return (
            <div className="font-family-medium font-size-small text-[var(--successGreen)]">
              Approved | ₹ {parentL1?.approvedAmount}
            </div>
          );
        }

        if (parentL1?.status === "Partially Approved")
          return (
            <div className="font-family-medium font-size-small text-[var(--pendingYellow)]">
              {parentL1?.status} | ₹ {parentL1?.approvedAmount}
            </div>
          );

        if (parentL1?.status === "Rejected")
          return (
            <div className="font-family-medium font-size-small text-[var(--textRed)]">
              {parentL1?.status}
            </div>
          );

        if (parentL1?.status === "Pending")
          return (
            <div className="font-family-medium font-size-small text-[var(--pendingYellow)]">
              {parentL1?.status}
            </div>
          );
        else
          return <div className="font-family-medium font-size-small">NA</div>;
      },
      Component: () => {
        // Show 0 if L1 is rejected
        const approvedAmount = (parentL1?.status === "Rejected" || parentL1?.action === "Rejected") ? 0 : parentL1?.approvedAmount;
        return (
          <Component data={{ ...parentL1, amount: outputData?.amount, approvedAmount }} />
        );
      },
    },
    {
      // Only enable L2 dropdown when L2 has actually taken action (not just "Partially Approved" with ₹0 from aggregation)
      isArrowEnable: parentL2 && parentL2?.status !== "Pending" && (
        (Number(parentL2?.approvedAmount) > 0) ||
        ["Rejected", "Rework Requested", "Rework Pending", "Approved", "Rework Approved"].includes(parentL2?.status)
      ),
      renderLeft: () => {
        return (
          <div className="font-family-medium font-size-small">
            L2 - Regional Manager
          </div>
        );
      },
      renderRight: () => {
        if (parentL2?.status === "Approved") {
          return (
            <div className="font-family-medium font-size-small text-[var(--successGreen)]">
              Approved | ₹ {parentL2?.approvedAmount}
            </div>
          );
        }

        if (parentL2?.status === "Rejected")
          return (
            <div className="font-family-medium font-size-small text-[var(--textRed)]">
              {parentL2?.status}
            </div>
          );

        if (parentL2?.status === "Partially Approved")
          return (
            <div className="font-family-medium font-size-small text-[var(--pendingYellow)]">
              {parentL2?.status} | ₹ {parentL2?.approvedAmount}
            </div>
          );

        if (parentL2?.status === "Pending")
          return (
            <div className="font-family-medium font-size-small text-[var(--pendingYellow)]">
              {parentL2?.status}
            </div>
          );
        else
          return <div className="font-family-medium font-size-small">NA</div>;
      },
      Component: () => {
        // Request Amount for L2 = amount received from L1. Ensure it's never less than L2 approved (avoids inconsistent "Approved > Request" when L1 total is under-reported e.g. after rework).
        const l1Approved = Number(parentL1?.approvedAmount) || 0;
        const l2Approved = Number(parentL2?.approvedAmount) || 0;
        const requestAmount = Math.max(l1Approved, l2Approved);
        // Show 0 if L2 is rejected
        const approvedAmount = (parentL2?.status === "Rejected" || parentL2?.action === "Rejected") ? 0 : parentL2?.approvedAmount;
        return (
          <Component data={{ ...parentL2, amount: requestAmount, approvedAmount }} />
        );
      },
    },
    {
      // Only enable L3 dropdown when L3 has actually taken action (not just "Partially Approved" with ₹0 from aggregation)
      isArrowEnable: parentL3 && parentL3?.status !== "Pending" && (
        (Number(parentL3?.approvedAmount) > 0) ||
        ["Rejected", "Rework Requested", "Rework Pending", "Approved", "Rework Approved"].includes(parentL3?.status)
      ),
      renderLeft: () => {
        return (
          <div className="font-family-medium font-size-small">
            L3 - Accounts Manager
          </div>
        );
      },
      renderRight: () => {
        if (parentL3?.status === "Approved") {
          return (
            <div className="font-family-medium font-size-small text-[var(--successGreen)]">
              Approved | ₹ {parentL3?.approvedAmount}
            </div>
          );
        }

        if (parentL3?.status === "Rejected")
          return (
            <div className="font-family-medium font-size-small text-[var(--textRed)]">
              {parentL3?.status}
            </div>
          );

        if (parentL3?.status === "Partially Approved")
          return (
            <div className="font-family-medium font-size-small text-[var(--pendingYellow)]">
              {parentL3?.status} | ₹ {parentL3?.approvedAmount}
            </div>
          );

        if (parentL3?.status === "Pending")
          return (
            <div className="font-family-medium font-size-small text-[var(--pendingYellow)]">
              {parentL3?.status}
            </div>
          );
        else
          return <div className="font-family-medium font-size-small">NA</div>;
      },
      Component: () => {
        // Request Amount for L3 = amount received from L2. Ensure it's never less than L3 approved (same consistency as L2).
        const l2Approved = Number(parentL2?.approvedAmount) || 0;
        const l3Approved = Number(parentL3?.approvedAmount) || 0;
        const requestAmount = Math.max(l2Approved, l3Approved);
        // Show 0 if L3 is rejected
        const approvedAmount = (parentL3?.status === "Rejected" || parentL3?.action === "Rejected") ? 0 : parentL3?.approvedAmount;
        return (
          <Component data={{ ...parentL3, amount: requestAmount, approvedAmount }} />
        );
      },
    },
  ];

  const isL1 = userLabel !== "Store";
  const isFinance = userLabel === "Finance";

  const voucherStatusApiCall = (payload, status, saveDecisionOnly = false) => {
    voucherStatusApi({
      url: apiUrls.dmsService.approveRequest,
      dataToSend: payload,
      method: "post",
      onSuccess: () => {
        setIsRejectVisible({ isOpen: false });
        setIsEditApproveVisible({ isOpen: false });
        setIsSendBackForReworkVisible({ isOpen: false });
        toast.success(status);
        
        // Update decision status map for saved decisions
        // Store optimistic updates to merge with API response
        if (saveDecisionOnly) {
          const optimisticDecisionMap = { ...decisionStatusMap };
          if (payload.approved && payload.approved.length > 0) {
            payload.approved.forEach((item) => {
              optimisticDecisionMap[item.id] = "Approved";
            });
          }
          if (payload.rejected && payload.rejected.length > 0) {
            payload.rejected.forEach((id) => {
              optimisticDecisionMap[id] = "Rejected";
            });
          }
          setDecisionStatusMap(optimisticDecisionMap);
          checkIfAllDecisionsMade(optimisticDecisionMap);
        }
        
        // Refresh data - updateDecisionStatusFromData will merge with optimistic updates
        updateVoucherData();
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || "Operation failed";
        toast.error(errorMessage);
        
        // If error mentions pending sub-vouchers, highlight them
        if (errorMessage.includes("Please complete all sub-voucher actions")) {
          toast.warning("Please complete all sub-voucher decisions before proceeding");
        }
      },
    });
  };

  const getIds = (rowData) => {
    const ids = [];
    if (rowData?.subRows) {
      rowData?.subRows?.forEach((row) => {
        if (row?.id) {
          ids.push(row?.id);

          return;
        }

        row?.subRows?.forEach((row) => {
          if (row?.id) {
            ids.push(row?.id);
            return;
          }

          row?.subRows?.forEach((row) => {
            if (row?.id) {
              ids.push(row?.id);
              return;
            }
          });
        });
      });
    } else {
      ids.push(rowData?.id);
    }
    return ids;
  };

  const getIdsForApproval = (rowData) => {
    const ids = [];
    if (rowData?.subRows) {
      rowData?.subRows?.forEach((row) => {
        if (row?.id) {
          ids.push({
            id: row?.id,
            approvedAmount: row?.amount,
            previousAm: row?.amount,
          });
          return;
        }

        // previousAm: row?.dataModalAttributeSet?.find(
        //   (item) => item.metadataTechnicalName == switchLabel(userActiveLevel)
        // )?.attributeValue,
        row?.subRows?.forEach((row) => {
          if (row?.id) {
            ids.push({
              id: row?.id,
              approvedAmount: row?.amount,
              previousAm: row?.amount,
            });
            return;
          }

          row?.subRows?.forEach((row) => {
            if (row?.id) {
              ids.push({
                id: row?.id,
                approvedAmount: row?.amount,
                previousAm: row?.amount,
              });
              return;
            }
          });
        });
      });
    } else {
      ids.push({
        id: rowData?.id,
        approvedAmount: approvedAmountPassAccordingToLevel(rowData),
        previousAm: approvedAmountPassAccordingToLevel(rowData),
      });
    }
    return ids;
  };

  const updateCommentRequestHandler = (params) => {
    let payload = {
      id: params?.id,
      voucherComment: params?.comment,
    };
    updateCommentApiCall(payload, "Voucher comment successfully updated.");
  };

  const updateCommentApiCall = (payload, status) => {
    voucherStatusApi({
      url: apiUrls.dmsService.updateVoucherComment,
      dataToSend: payload,
      method: "post",
      onSuccess: () => {
        setIsEditVoucherCommentVisible({ isOpen: false });
        toast.success(status);
        updateVoucherData();
      },
    });
  };

  const updateVoucherData = (onComplete, skipNavigation = false) => {
    // When vouchers transition to/from rework, we need to refresh the canSendAll state
    // This will be called after updateVoucherData completes via useEffect
    voucherStatusApi({
      url: apiUrls.dmsService.getVoucherDetails + vid,
      method: "post",
      onSuccess: (apiData) => {
        const nextType = state?.type || apiData?.type || "Rework";
        setDataState({ ...apiData, type: nextType });
        
        // Only navigate if not skipping (for Store users or after edit, skip navigation to prevent reload)
        if (!skipNavigation) {
        navigate(".", {
          replace: true,
            state: { ...apiData, type: nextType },
          });
        } else {
          // For Store users, update sessionStorage to preserve state
          try {
            sessionStorage.setItem(`voucher_${vid}_state`, JSON.stringify({ ...apiData, type: nextType }));
          } catch (e) {
            // console.error("Error saving to sessionStorage:", e);
          }
        }
        
        // Don't call updateDecisionStatusFromData here - useEffect will handle it
        // This prevents double updates and race conditions
        
        // Call completion callback if provided
        if (onComplete) {
          onComplete();
        }
      },
      onError: (error) => {
        // console.error("Error fetching voucher details:", error);
        const errorMessage = error?.response?.data?.message || "Failed to fetch voucher details";
        toast.error(errorMessage);
        
        // Call completion callback even on error
        if (onComplete) {
          onComplete();
        }
      },
    });
  };

  // Update decision status map from API data
  // Merges API data with optimistic updates to prevent race conditions
  const updateDecisionStatusFromData = (apiData) => {
    try {
      // Preserve optimistic updates that might not be in API response yet
      const mergedDecisionMap = { ...decisionStatusMap };
      const allDocs = [
        ...(apiData?.pendingDocs || []),
        ...(apiData?.approvedDocs || []),
        ...(apiData?.rejectedDocs || []),
        ...(apiData?.pendingApprovedRejectedDocs || []),
      ];
      
      if (!Array.isArray(allDocs)) {
        // console.warn("updateDecisionStatusFromData: allDocs is not an array", allDocs);
        return;
      }
      
      allDocs.forEach((doc) => {
        if (!doc || !doc.id) {
          return; // Skip invalid docs
        }
        
        // Check if sub-voucher has a decision made by checking the current step's action
        // When saveDecisionOnly=true, backend sets currentStepInstance.action but doesn't advance workflow
        // So we need to check the action on the current step for the acting user level
        
        // First, try to find the current step for this user level in workflowStepResponses
        // The current step should have an action set when saveDecisionOnly=true
        let currentStep = null;
        
        if (doc?.workflowStepResponses && Array.isArray(doc.workflowStepResponses)) {
          // Find step matching user level - prioritize steps with actions set (saved decisions)
          // NOTE: Backend maps action to 'status' field in WorkflowStepResponse
          currentStep = doc.workflowStepResponses.find(
            (step) => step?.userLabel === userLabel && 
                     (step.status === "Approved" || 
                      step.status === "Rejected" || 
                      step.status === "ReworkApproved" || 
                      step.status === "Partially Approved" ||
                      // Also check action field for backward compatibility
                      step.action === "Approved" || 
                      step.action === "Rejected" || 
                      step.action === "ReworkApproved" || 
                      step.action === "Partially Approved")
          );
          
          // If no step with action found, find any step for this user level (might be pending)
          if (!currentStep) {
            currentStep = doc.workflowStepResponses.find(
              (step) => step?.userLabel === userLabel
            );
          }
        }
        
        // Check current step's action/status (this is set when saveDecisionOnly=true)
        // Backend maps action to 'status' field, so check status first, then action for backward compatibility
        const stepAction = currentStep?.status || currentStep?.action;
        const stepActionLower = stepAction?.toLowerCase();
        
        // CRITICAL: Always check status fields because "Partially Approved" is computed on doc.getL2().status
        // but workflowStepResponse.status stays "Approved", so we MUST check status fields to catch it
        let status = null;
        if (userLabel === "L1") {
          status = doc?.l1?.status || doc?.newL1Status;
        } else if (userLabel === "L2") {
          status = doc?.l2?.status || doc?.newL2Status;
        } else if (userLabel === "Finance") {
          status = doc?.l3?.status || doc?.newL3Status || doc?.Finance?.status;
        } else {
          status = doc?.l1?.status || doc?.l2?.status || doc?.l3?.status || doc?.newL1Status || doc?.newL2Status || doc?.newL3Status;
        }
        
        // Check status fields first (this catches "Partially Approved" computed by backend)
        if (status) {
          const statusLower = status.toLowerCase();
          if (["approved", "reworkapproved", "rework approved", "partially approved", "partiallyapproved"].includes(statusLower)) {
            mergedDecisionMap[doc.id] = "Approved";
          } else if (statusLower === "rejected") {
            mergedDecisionMap[doc.id] = "Rejected";
          }
        }
        
        // Fallback: Check step action if status fields didn't have a decision
        // This handles cases where status fields might not be populated yet
        if (!mergedDecisionMap[doc.id]) {
          if (stepActionLower && ["approved", "reworkapproved", "rework approved", "partially approved", "partiallyapproved"].includes(stepActionLower)) {
            mergedDecisionMap[doc.id] = "Approved";
          } else if (stepActionLower === "rejected") {
            mergedDecisionMap[doc.id] = "Rejected";
          }
        }
      });
      
      setDecisionStatusMap(mergedDecisionMap);
      checkIfAllDecisionsMade(mergedDecisionMap);
    } catch (error) {
      // console.error("Error in updateDecisionStatusFromData:", error);
      // Don't break the app - just log the error
    }
  };

  // Helper function to check if a voucher is in rework state
  // This prevents bulk send when vouchers are going backward (rework)
  const checkIfVoucherInRework = (sub) => {
    // CRITICAL: Check status fields FIRST - if status is "Rework Approved", rework is COMPLETE
    // Vouchers with "Rework Approved" status are NOT in rework anymore
    const statusFields = [
      sub?.status,
      sub?.L1?.status,
      sub?.L2?.status,
      sub?.L3?.status,
      sub?.Finance?.status,
      sub?.newL1Status,
      sub?.newL2Status,
      sub?.newL3Status
    ];
    
    for (const status of statusFields) {
      if (status) {
        const statusLower = status.toLowerCase();
        // If status is "Rework Approved" or "ReworkApproved", rework is COMPLETE - voucher is NOT in rework
        if (statusLower === "rework approved" || statusLower === "reworkapproved") {
          return false; // Rework is complete, voucher is NOT in rework
        }
        // Check for active rework states
        if (
          status.includes("Rework Requested") ||
          status.includes("Rework Pending") ||
          status.includes("ReworkRequested") ||
          status.includes("ReworkPending")
        ) {
          return true;
        }
      }
    }

    // Check approval_status metadata field
    const approvalStatusAttr = sub?.dataModalAttributeSet?.find(
      (attr) => attr?.metadataTechnicalName === "approval_status"
    );
    const approvalStatus = approvalStatusAttr?.attributeValue;
    
    if (approvalStatus && (
      approvalStatus.includes("ReworkRequested") || 
      approvalStatus.includes("ReworkPending")
    )) {
      return true;
    }

    // Check workflow step responses for rework status
    // IMPORTANT: Check the LATEST/MOST RECENT step, not all steps
    // If latest step is "ReworkApproved", rework is complete
    if (sub?.workflowStepResponses && Array.isArray(sub.workflowStepResponses)) {
      // Get the most recent step (last in array, or step with latest actionDate)
      const steps = [...sub.workflowStepResponses];
      const latestStep = steps.length > 0 ? steps[steps.length - 1] : null;
      
      // If latest step is ReworkApproved, rework is complete
      if (latestStep) {
        const latestStatus = latestStep?.status || latestStep?.action;
        if (latestStatus && (
          latestStatus === "ReworkApproved" || 
          latestStatus.toLowerCase() === "reworkapproved" ||
          latestStatus === "Rework Approved"
        )) {
          return false; // Rework is complete
        }
      }
      
      // Check all steps for active rework states
      for (const step of sub.workflowStepResponses) {
        const stepStatus = step?.status;
        if (stepStatus && (
          stepStatus.includes("ReworkRequested") ||
          stepStatus.includes("ReworkPending") ||
          stepStatus === "L1ReworkRequested" ||
          stepStatus === "L1ReworkPending" ||
          stepStatus === "L2ReworkRequested" ||
          stepStatus === "L2ReworkPending" ||
          stepStatus === "AccountsReworkRequested" ||
          stepStatus === "AccountsReworkPending"
        )) {
          return true;
        }
        
        // Check action field for rework indicators (but exclude ReworkApproved)
        const action = step?.action;
        if (action && action !== "ReworkApproved" && (
          action.toUpperCase().includes("REWORK") ||
          action === "REWORK_STARTED" ||
          action === "REWORK_REQUESTED"
        )) {
          return true;
        }
      }
    }

    return false;
  };

  // Helper to extract selected vouchers from checkboxes
  const getSelectedVouchers = () => {
    const selectedVouchers = [];
    if (!selectedCheckBoxes || selectedCheckBoxes.length === 0) {
      return selectedVouchers;
    }
    
    selectedCheckBoxes.forEach((row) => {
      if (row?.original?.subRows) {
        row?.original?.subRows?.forEach((subRow) => {
          if (subRow?.id) {
            selectedVouchers.push(subRow);
          }
          subRow?.subRows?.forEach((nestedRow) => {
            if (nestedRow?.id) {
              selectedVouchers.push(nestedRow);
            }
          });
        });
      } else if (row?.original?.id) {
        selectedVouchers.push(row.original);
      }
    });
    
    return selectedVouchers;
  };

  // Helper to get vouchers to process based on user level
  // L1 users: use selected vouchers from checkboxes
  // L2/Finance users: use all vouchers from outputData
  const getVouchersToProcess = () => {
    if (userLabel === "L1") {
      return getSelectedVouchers();
    } else {
      // For L2/Finance: get all vouchers from outputData
      const allVouchers = Array.isArray(outputData) ? outputData : [];
      return allVouchers.filter((sub) => !sub.subRows || sub.subRows.length === 0);
    }
  };

  // Helper to check if a voucher is already approved at user's level
  const isVoucherApproved = (voucher) => {
    // Check decision status map first
    const decision = decisionStatusMap[voucher?.id];
    if (decision === "Approved") {
      return true;
    }
    
    // Check workflow step responses
    if (voucher?.workflowStepResponses && Array.isArray(voucher.workflowStepResponses)) {
      const userStep = voucher.workflowStepResponses.find(
        (step) => step.userLabel === userLabel
      );
      // Check both status and action fields (backend maps action to status)
      const stepAction = userStep?.status || userStep?.action;
      if (stepAction && ["Approved", "ReworkApproved", "Partially Approved"].includes(stepAction)) {
        return true;
      }
    }
    
    // Check status fields based on user level
    let status = null;
    if (userLabel === "L1") {
      status = voucher?.newL1Status || voucher?.L1?.status;
    } else if (userLabel === "L2") {
      status = voucher?.newL2Status || voucher?.L2?.status;
    } else if (userLabel === "Finance") {
      status = voucher?.newL3Status || voucher?.L3?.status || voucher?.Finance?.status;
    }
    
    if (status && ["Approved", "ReworkApproved", "Partially Approved"].includes(status)) {
      return true;
    }
    
    return false;
  };

  // Helper to check if a voucher is already rejected at user's level
  const isVoucherRejected = (voucher) => {
    // Check decision status map first
    const decision = decisionStatusMap[voucher?.id];
    if (decision === "Rejected") {
      return true;
    }
    
    // Check workflow step responses
    if (voucher?.workflowStepResponses && Array.isArray(voucher.workflowStepResponses)) {
      const userStep = voucher.workflowStepResponses.find(
        (step) => step.userLabel === userLabel
      );
      // Check both status and action fields (backend maps action to status)
      const stepAction = userStep?.status || userStep?.action;
      if (stepAction === "Rejected") {
        return true;
      }
    }
    
    // Check status fields based on user level
    let status = null;
    if (userLabel === "L1") {
      status = voucher?.newL1Status || voucher?.L1?.status;
    } else if (userLabel === "L2") {
      status = voucher?.newL2Status || voucher?.L2?.status;
    } else if (userLabel === "Finance") {
      status = voucher?.newL3Status || voucher?.L3?.status || voucher?.Finance?.status;
    }
    
    if (status === "Rejected") {
      return true;
    }
    
    return false;
  };

  // Helper to check if selected vouchers can be sent back for rework
  // Must be defined after checkIfVoucherInRework
  const canSendSelectedBackForRework = () => {
    // For L1 users: check selected vouchers (they have checkboxes)
    // For L2/Finance users: check all vouchers in outputData (no checkboxes)
    let vouchersToCheck = [];
    
    if (userLabel === "L1") {
      vouchersToCheck = getSelectedVouchers();
      if (vouchersToCheck.length === 0) {
        return false;
      }
    } else {
      // For L2/Finance: check all vouchers in outputData
      const allVouchers = Array.isArray(outputData) ? outputData : [];
      vouchersToCheck = allVouchers.filter((sub) => !sub.subRows || sub.subRows.length === 0);
      if (vouchersToCheck.length === 0) {
        return false;
      }
    }
    
    // Return true if at least one voucher can be sent back for rework
    return vouchersToCheck.some((voucher) => {
      // If voucher is already in rework, cannot send it back again
      if (checkIfVoucherInRework(voucher)) {
        return false;
      }
      
      // Check if voucher can be sent back for rework at user's level
      // Voucher must be pending at user's level and not already approved/rejected
      return canSendBackForRework(voucher, userLabel);
    });
  };

  // Helper to check if selected vouchers can be approved
  const canApproveSelectedVouchers = () => {
    // For L1 users: check selected vouchers (they have checkboxes)
    // For L2/Finance users: check all vouchers in outputData (no checkboxes)
    let vouchersToCheck = [];
    
    if (userLabel === "L1") {
      vouchersToCheck = getSelectedVouchers();
      if (vouchersToCheck.length === 0) {
        return false;
      }
    } else {
      // For L2/Finance: check all vouchers in outputData
      const allVouchers = Array.isArray(outputData) ? outputData : [];
      vouchersToCheck = allVouchers.filter((sub) => !sub.subRows || sub.subRows.length === 0);
      if (vouchersToCheck.length === 0) {
        return false;
      }
    }
    
    // Return true if at least one voucher can be approved
    // (i.e., not already approved, not rejected, and not in rework)
    return vouchersToCheck.some((voucher) => {
      return !isVoucherApproved(voucher) && 
             !isVoucherRejected(voucher) && 
             !checkIfVoucherInRework(voucher);
    });
  };

  // Helper to check if selected vouchers can be rejected
  const canRejectSelectedVouchers = () => {
    // For L1 users: check selected vouchers (they have checkboxes)
    // For L2/Finance users: check all vouchers in outputData (no checkboxes)
    let vouchersToCheck = [];
    
    if (userLabel === "L1") {
      vouchersToCheck = getSelectedVouchers();
      if (vouchersToCheck.length === 0) {
        return false;
      }
    } else {
      // For L2/Finance: check all vouchers in outputData
      const allVouchers = Array.isArray(outputData) ? outputData : [];
      vouchersToCheck = allVouchers.filter((sub) => !sub.subRows || sub.subRows.length === 0);
      if (vouchersToCheck.length === 0) {
        return false;
      }
    }
    
    // Return true if at least one voucher can be rejected
    // (i.e., not already rejected, not approved, and not in rework)
    return vouchersToCheck.some((voucher) => {
      return !isVoucherRejected(voucher) && 
             !isVoucherApproved(voucher) && 
             !checkIfVoucherInRework(voucher);
    });
  };

  // Check if all sub-vouchers have decisions made
  const checkIfAllDecisionsMade = (decisionMap = decisionStatusMap) => {
    const subVouchers = Array.isArray(outputData) ? outputData : [];
    if (!subVouchers.length) {
      setCanSendAll(false);
      return;
    }

    const actualSubVouchers = subVouchers.filter((sub) => !sub.subRows || sub.subRows.length === 0);
    if (actualSubVouchers.length === 0) {
      setCanSendAll(false);
      return;
    }

    const allHaveDecisions = actualSubVouchers.every((sub) => {
      const decision = decisionMap[sub.id];
      const statusForLevel = userLabel === "L1" ? (sub?.newL1Status || sub?.L1?.status || sub?.l1?.status)
        : userLabel === "L2" ? (sub?.newL2Status || sub?.L2?.status || sub?.l2?.status)
        : (sub?.newL3Status || sub?.L3?.status || sub?.l3?.status || sub?.Finance?.status);
      const statusLower = (statusForLevel || "").toLowerCase().trim();
      const hasDecisionAtLevel = decision === "Approved" || decision === "Rejected"
        || ["approved", "reworkapproved", "partially approved", "partiallyapproved"].includes(statusLower)
        || statusLower === "rejected" || statusLower === "stopped";

      if (hasDecisionAtLevel) return true;

      const isInRework = checkIfVoucherInRework(sub);
      if (isInRework) return false;

      const approvalStatusAttr = sub?.dataModalAttributeSet?.find(
        (attr) => attr?.metadataTechnicalName === "approval_status"
      );
      const approvalStatus = approvalStatusAttr?.attributeValue;
      if (approvalStatus) {
        const approvalStatusUpper = approvalStatus.toUpperCase();
        if (userLabel === "L1" && (
          approvalStatusUpper.includes("L2APPROVALPENDING") ||
          approvalStatusUpper.includes("ACCOUNTSAPPROVALPENDING") ||
          approvalStatusUpper.includes("L2REJECTED") ||
          approvalStatusUpper.includes("ACCOUNTSREJECTED")
        )) return true;
        if (userLabel === "L2" && (
          approvalStatusUpper.includes("ACCOUNTSAPPROVALPENDING") ||
          approvalStatusUpper.includes("ACCOUNTSREJECTED")
        )) return true;
        if (userLabel === "L2" && approvalStatusUpper.includes("L1REJECTED")) return true;
        if (userLabel === "L2" && (
          approvalStatusUpper.includes("L1APPROVALPENDING") ||
          approvalStatusUpper.includes("L1REWORKREQUESTED") ||
          approvalStatusUpper.includes("L1REWORKPENDING")
        )) return false;
        if (userLabel === "Finance" && (
          approvalStatusUpper.includes("L1REJECTED") ||
          approvalStatusUpper.includes("L2REJECTED")
        )) return true;
        if (userLabel === "Finance" && (
          approvalStatusUpper.includes("L1APPROVALPENDING") ||
          approvalStatusUpper.includes("L1REWORKREQUESTED") ||
          approvalStatusUpper.includes("L1REWORKPENDING") ||
          approvalStatusUpper.includes("L2APPROVALPENDING") ||
          approvalStatusUpper.includes("L2REWORKREQUESTED") ||
          approvalStatusUpper.includes("L2REWORKPENDING")
        )) return false;
      }

      if (decision === "Approved" || decision === "Rejected") return true;

      let currentStep = null;
      if (sub?.workflowStepResponses && Array.isArray(sub.workflowStepResponses)) {
        currentStep = sub.workflowStepResponses.find(
          (step) => step.userLabel === userLabel && (
            step.status === "Approved" || step.status === "Rejected" || step.status === "STOPPED" ||
            step.status === "ReworkApproved" || step.status === "Partially Approved" ||
            step.action === "Approved" || step.action === "Rejected" || step.action === "STOPPED" ||
            step.action === "ReworkApproved" || step.action === "Partially Approved"
          )
        );
        if (!currentStep) {
          const stepsForUser = sub.workflowStepResponses.filter((step) => step.userLabel === userLabel);
          if (stepsForUser.length > 0) {
            const latestStep = stepsForUser[stepsForUser.length - 1];
            if (latestStep && (
              latestStep.status === "ReworkApproved" || latestStep.action === "ReworkApproved" ||
              latestStep.status === "Approved" || latestStep.action === "Approved" ||
              latestStep.status === "Partially Approved" || latestStep.action === "Partially Approved"
            )) currentStep = latestStep;
          }
        }
        if (!currentStep) {
          currentStep = sub.workflowStepResponses.find((step) => step.userLabel === userLabel);
        }
      }

      let status;
      if (userLabel === "L1") status = sub?.newL1Status || sub?.L1?.status || sub?.l1?.status;
      else if (userLabel === "L2") status = sub?.newL2Status || sub?.L2?.status || sub?.l2?.status;
      else if (userLabel === "Finance") status = sub?.newL3Status || sub?.L3?.status || sub?.l3?.status || sub?.Finance?.status || sub?.finance?.status;
      else status = sub?.newL1Status || sub?.newL2Status || sub?.newL3Status || sub?.L1?.status || sub?.L2?.status || sub?.L3?.status || sub?.l1?.status || sub?.l2?.status || sub?.l3?.status;

      if (status) {
        const s = status.toLowerCase().trim();
        if (["approved", "reworkapproved", "rework approved", "partially approved", "partiallyapproved"].includes(s)) return true;
        if (s === "rejected" || s === "stopped") return true;
      }

      const action = currentStep?.status || currentStep?.action;
      if (action && ["approved", "reworkapproved", "rework approved", "partially approved", "partiallyapproved"].includes(action.toLowerCase())) return true;
      if (action && (action.toLowerCase() === "rejected" || action.toLowerCase() === "stopped")) return true;

      return false;
    });

    setCanSendAll(allHaveDecisions);
  };

  // Helper function to track voucher status before sending
  const trackVoucherStatuses = (voucherData) => {
    const statusTracking = {
      timestamp: new Date().toISOString(),
      voucherId: vid,
      parentVoucher: {
        id: voucherData?.id,
        status: voucherData?.status,
        amount: voucherData?.amount,
      },
      subVouchers: [],
      summary: {
        total: 0,
        approved: 0,
        rejected: 0,
        pending: 0,
        inRework: 0,
      }
    };

    // Collect all sub-vouchers from different document lists
    const allSubVouchers = [
      ...(voucherData?.pendingDocs || []),
      ...(voucherData?.approvedDocs || []),
      ...(voucherData?.rejectedDocs || []),
      ...(voucherData?.pendingApprovedRejectedDocs || []),
    ];

    allSubVouchers.forEach((subVoucher) => {
      const subStatus = {
        id: subVoucher?.id,
        documentName: subVoucher?.documentName,
        amount: subVoucher?.dataModalAttributeSet?.find(
          (attr) => attr?.metadataTechnicalName === "amount"
        )?.attributeValue || 0,
        status: subVoucher?.status,
        l1Status: subVoucher?.l1?.status,
        l2Status: subVoucher?.l2?.status,
        l3Status: subVoucher?.l3?.status,
        approvalStatus: subVoucher?.dataModalAttributeSet?.find(
          (attr) => attr?.metadataTechnicalName === "approval_status"
        )?.attributeValue,
        workflowSteps: subVoucher?.workflowStepResponses?.map((step) => ({
          userLabel: step?.userLabel,
          status: step?.status,
          action: step?.action,
        })) || [],
        isInRework: checkIfVoucherInRework(subVoucher),
      };

      statusTracking.subVouchers.push(subStatus);
      statusTracking.summary.total++;

      // Categorize by status
      if (subStatus.isInRework) {
        statusTracking.summary.inRework++;
      } else if (subStatus.l1Status === "Approved" || subStatus.l2Status === "Approved" || subStatus.l3Status === "Approved") {
        statusTracking.summary.approved++;
      } else if (subStatus.l1Status === "Rejected" || subStatus.l2Status === "Rejected" || subStatus.l3Status === "Rejected") {
        statusTracking.summary.rejected++;
      } else {
        statusTracking.summary.pending++;
      }
    });

    // Log for tracking/debugging
    // console.log("Voucher Status Tracking Before Send All:", statusTracking);
    
    return statusTracking;
  };

  // Handler for Send All to Next Level
  const handleSendAllToNextLevel = () => {
    if (!vid) {
      toast.error("Voucher ID not found");
      return;
    }

    const isL2OrFinance = userLabel === "L2" || userLabel === "Finance";

    // For L2/Finance: backend canSubmitVoucher is the single source of truth; skip local rework count
    if (isL2OrFinance) {
      voucherStatusApi({
        url: `${apiUrls.dmsService.canSubmitVoucher}/${vid}`,
        method: "get",
        onSuccess: (canSubmit) => {
          if (canSubmit) {
            proceedWithSendAll();
          } else {
            toast.error(
              "Cannot send all: one or more sub-vouchers are in rework or do not have a final decision. " +
              "Please wait for rework to be completed and resubmitted before sending."
            );
            updateVoucherData();
          }
        },
        onError: () => {
          toast.error("Unable to verify if voucher can be submitted. Please try again.");
          updateVoucherData();
        },
      });
      return;
    }

    // L1: Pre-check using local data (no canSubmitVoucher API for L1)
    const subVouchers = Array.isArray(outputData) ? outputData : [];
    const actualSubVouchers = subVouchers.filter((sub) => !sub.subRows || sub.subRows.length === 0);
    const vouchersInRework = actualSubVouchers.filter((sub) => checkIfVoucherInRework(sub));
    if (vouchersInRework.length > 0) {
      toast.error(
        `Cannot send all: ${vouchersInRework.length} sub-voucher(s) are in rework state. ` +
        "Please wait for rework to be completed and resubmitted before sending."
      );
      updateVoucherData();
      return;
    }

    proceedWithSendAll();

    function proceedWithSendAll() {
      // Step 1: First fetch latest voucher details to track status before sending
      voucherStatusApi({
        url: apiUrls.dmsService.getVoucherDetails + vid,
        method: "post",
        onSuccess: (voucherData) => {
          // Track status of each voucher before sending
          trackVoucherStatuses(voucherData);

          // For L2/Finance, backend canSubmitVoucher is source of truth - skip local rework check
          if (!isL2OrFinance) {
            const allDocs = [
              ...(voucherData?.pendingDocs || []),
              ...(voucherData?.approvedDocs || []),
              ...(voucherData?.rejectedDocs || []),
              ...(voucherData?.pendingApprovedRejectedDocs || []),
            ];
            const stillInRework = allDocs.filter((doc) => checkIfVoucherInRework(doc));
            if (stillInRework.length > 0) {
              toast.error(
                `Cannot send all: ${stillInRework.length} sub-voucher(s) are in rework state. ` +
                "Status has changed. Please refresh and try again."
              );
              const nextType = state?.type || voucherData?.type || "Rework";
              setDataState({ ...voucherData, type: nextType });
              return;
            }
          }

          // Step 2: Proceed with sending all to next level
          voucherStatusApi({
            url: `${apiUrls.dmsService.sendAllToNextLevel}/${vid}`,
            method: "post",
            dataToSend: {},
            onSuccess: () => {
              toast.success(
                userLabel === "Finance"
                  ? "All sub-vouchers Submitted successfully"
                  : "All sub-vouchers sent to next level successfully"
              );
              setCanSendAll(false);
              updateVoucherData(() => {
                setDecisionStatusMap({});
              });
            },
            onError: (error) => {
              const errorMessage = error?.response?.data?.message || "Failed to send all to next level";
              toast.error(errorMessage);
              if (errorMessage.includes("rework") || errorMessage.includes("Rework")) {
                toast.warning(
                  "Some vouchers are in rework state. Please wait for rework to be completed before sending."
                );
                updateVoucherData();
              } else if (errorMessage.includes("Please complete all sub-voucher actions")) {
                toast.warning("Please complete all sub-voucher decisions before proceeding");
                updateVoucherData();
              }
            },
          });
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || "Failed to fetch voucher details";
        toast.error(`Cannot proceed: ${errorMessage}`);
        // console.error("Error fetching voucher details before send all:", error);
      },
    });
    }
  };

  const approveRequestHandler = (ids = [], comment = "", saveDecisionOnly = true) => {
    let payload = {
      approved: ids?.map((i) => ({
        id: i.id,
        approvedAmount: i.approvedAmount,
      })),
      comment,
      rejected: [],
      saveDecisionOnly: saveDecisionOnly, // Save decision but don't advance workflow
    };
    voucherStatusApiCall(payload, "Decision saved successfully", saveDecisionOnly);
  };

  const rejectRequestHandler = (ids, comment = "", saveDecisionOnly = true) => {
    let payload = {
      approved: [],
      comment: comment,
      rejected: ids,
      saveDecisionOnly: saveDecisionOnly, // Save decision but don't advance workflow
    };
    voucherStatusApiCall(payload, "Decision saved successfully", saveDecisionOnly);
  };

  const handleEditAfterRework = useCallback((rowData) => {
    // For store users, use the parent voucher data (state) instead of individual row data
    // The voucher ID comes from vid param, and we need the full voucher object
    const voucherDataForEdit = userLabel === "Store" 
      ? { ...state, id: vid, vid: vid, head: rowData?.head, comment: rowData?.comment }
      : { ...rowData, id: rowData?.id || vid, vid: vid };
    
    setIsEditAfterReworkVisible({
      isOpen: true,
      data: voucherDataForEdit,
    });
  }, [userLabel, state, vid]);

  const editAfterReworkHandler = (payload, onComplete) => {
    voucherStatusApi({
      url: apiUrls.dmsService.editVoucherAfterRework,
      dataToSend: payload,
      method: "post",
      onSuccess: (apiData) => {
        toast.success("Voucher updated successfully");
        setIsEditAfterReworkVisible({ isOpen: false, data: {} });
        // Store: always refetch voucher details so all line items (including remaining rework items) are shown
        if (userLabel === "Store") {
          updateVoucherData(() => {
            if (onComplete) onComplete();
          }, true);
          return;
        }
        const hasVoucherDetailShape = apiData && (
          Array.isArray(apiData.pendingDocs) ||
          Array.isArray(apiData.approvedDocs) ||
          Array.isArray(apiData.reworkRequestedDocs)
        );
        if (hasVoucherDetailShape) {
          const nextType = state?.type || apiData?.type || "Rework";
          const newState = { ...apiData, type: nextType };
          setDataState(newState);
          try {
            sessionStorage.setItem(`voucher_${vid}_state`, JSON.stringify(newState));
          } catch (e) {
            // console.error("Error saving to sessionStorage:", e);
          }
          if (onComplete) onComplete();
        } else {
          updateVoucherData(() => {
            if (onComplete) onComplete();
          }, true);
        }
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || "Failed to update voucher");
        if (onComplete) onComplete();
      },
    });
  };

  const sendBackForReworkHandler = (rowData = [], comment = "") => {
    const ids = Array.isArray(rowData) ? rowData : getIds([rowData]);
    let payload = {
      approved: [],
      rejected: [],
      reworkRequested: ids.map((i) => i.id || i),
      comment: comment,
    };
    voucherStatusApi({
      url: apiUrls.dmsService.sendBackForRework,
      dataToSend: payload,
      method: "post",
      onSuccess: () => {
        setIsEditApproveVisible({ isOpen: false });
        setIsRejectVisible({ isOpen: false });
        setIsSendBackForReworkVisible({ isOpen: false });
        toast.success("Sent Back for Rework Successfully");
        updateVoucherData();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || "Failed to send back for rework");
      },
    });
  };

  const onClickViewFile = useCallback((file_id, rowData) => {
    fileDownload({
      url: apiUrls.dmsService.getFile + "/" + file_id,
      method: "get",
      responseType: "blob",
      onSuccess: (apiData) => {
        setDownloadPreviewOpenWithData({
          ...rowData,
          file: apiData.data,
          isOpen: true,
          file_id: file_id,
        });
      },
    });
  }, [fileDownload]);

  const memoStoreColumns = useMemo(
    () => adminColumns({ 
      onClickDownloadFile, 
      onEditClick: handleEditAfterRework,
      userLabel,
      voucherData: state,
      vid,
      onReworkHistoryClick: handleReworkHistoryClick, // Pass rework history handler for Store users too
    }),
    [userLabel, state, outputData, vid, onClickDownloadFile, handleEditAfterRework, handleReworkHistoryClick] // Include all dependencies
  );

  const getFile = useCallback((file_id, onSuccess) => {
    fileDownload({
      url: apiUrls.dmsService.getFile + "/" + file_id,
      method: "get",
      responseType: "blob",
      onSuccess,
    });
  }, [fileDownload]);

  const handleReworkHistoryClick = useCallback((voucherId) => {
    setIsReworkHistoryVisible({
      isOpen: true,
      voucherId: voucherId,
    });
  }, []);

  const nonStoreColumns = useMemo(
    () =>
      subRow2Data?.length
        ? areaManagerNestedSubheadColumns({ onClickDownloadFile, vid })
        : subRow1Data?.length
        ? areaManagerSubheadColumns()
        : areaManagerColumns({
            onClickViewFile,
            vid,
            userLabel,
            voucherType: state?.type,
            setIsEditVoucherCommentVisible,
            getFile,
            decisionStatusMap, // Pass decision status map to columns
            onReworkHistoryClick: handleReworkHistoryClick, // Pass rework history handler
          }),
    [subRow1Data, subRow2Data, userLabel, decisionStatusMap, onClickDownloadFile, vid, onClickViewFile, state?.type, setIsEditVoucherCommentVisible, getFile, handleReworkHistoryClick]
  );

  return (
    <div className="dashboard-container">
      <ToolbarHeader
        showBack
        renderLeft={() => {
          return (
            <div
              className={`flex items-center ${
                isMobileSmall ? "text-[2rem]" : "text-[3rem]"
              } font-family-medium`}
            >
              <div className="text-[var(--blackColor)]">Voucher ID: {vid}</div>
            </div>
          );
        }}
        containerClassName="mt-14"
        onBackClick={() => {
          if (subRow2Data?.length) {
            setSubRow2Data([]);
            return;
          }
          if (subRow1Data?.length) {
            setSubRow1Data([]);
            return;
          }
          navigate(-1);
        }}
      />

      <div
        className={`ml-16 mb-6 ${
          isMobileSmall ? "text-[2rem]" : "text-[3rem]"
        }`}
      >
        {!isL1 && outputData?.status && (
          <div className="text-[var(--pendingYellow)]">
            {outputData?.status}
          </div>
        )}
        <div className="font-family-medium text-[var(--blackColor)]">
          Amount: ₹{" "}
          {state?.type === "Approved"
            ? state?.approvedAmount
            : state?.type === "Pending"
            ? (calculateTotalApprovedAmount !== null ? calculateTotalApprovedAmount : state?.pendingAmount)
            : state?.type === "Rejected"
            ? state?.rejectedAmount
            : state?.type === "All"
            ? state?.pendingAmount === 0
              ? state?.approvedAmount
              : (calculateTotalApprovedAmount !== null ? calculateTotalApprovedAmount : state?.pendingAmount)
            : (calculateTotalApprovedAmount !== null ? calculateTotalApprovedAmount : outputData?.amount)}
        </div>
      </div>

      {!isL1 && (
        <CustomAccordion
          items={accordionItems}
          activeIndex={activeAccordion}
          setActiveIndex={setActiveAccordion}
        />
      )}

      <div className="mt-4">
        <DetailsCard
          rows={isL1 ? nonStoreColumns : memoStoreColumns}
          data={
            isL1
              ? subRow2Data?.length
                ? subRow2Data
                : subRow1Data?.length
                ? subRow1Data
                : outputData
              : storeTableData
          }
          customRow={(row, rowData) => {
            if (userLabel !== "Store" && row.Header === "Sub Head") {
              return (
                <div
                  className={`min-h-[5rem] bg-[var(--darkGray2)] flex items-center justify-between px-5 py-2 text-[var(--whiteColor)] rounded-tl-[1rem] rounded-tr-[1rem]`}
                  onClick={() => {
                    if (!rowData?.id) {
                      setSubRow2Data(rowData?.subRows);
                    }
                  }}
                >
                  <div className="w-[80%] font-size-medium font-family-medium">
                    {rowData?.[row?.accessor]}
                  </div>

                  {rowData?.filename ? (
                    <img
                      src={icons.squareEyeIcon}
                      alt="view attachment"
                      className="cursor-pointer w-[26px] h-[26px]"
                      onClick={() => onClickViewFile(rowData?.id, rowData)}
                    />
                  ) : (
                    <FaChevronRight color="var(--whiteColor)" size={15} />
                  )}
                </div>
              );
            }
            if (userLabel !== "Store" && row.Header === "Voucher Head") {
              return (
                <div
                  className={`min-h-[5rem] bg-[var(--darkGray2)] flex items-center justify-between px-5 py-2 text-[var(--whiteColor)] rounded-tl-[1rem] rounded-tr-[1rem]`}
                  onClick={() => {
                    setSubRow1Data(rowData?.subRows);
                  }}
                >
                  <div className="min-w-[18rem] font-size-medium font-family-medium">
                    {rowData?.[row?.accessor]}
                  </div>

                  {/* <FaChevronRight color="var(--whiteColor)" size={15} /> */}
                </div>
              );
            }
          }}
          sendRowDataInHandler
          isRejectApproveButtonEnable={
            userLabel !== "Store" && state?.type === "Pending"
          }
          userLabel={userLabel}
          rejectHandler={(rowData) => {
            const ids = getIds([rowData]);
            setIsRejectVisible({ data: ids, isOpen: true });
          }}
          approveHandler={(rowData) => {
            const ids = getIdsForApproval(rowData);
            approveRequestHandler(ids);
          }}
          editApproveHandler={(rowData) => {
            const ids = getIdsForApproval(rowData);
            setIsEditApproveVisible({
              data: rowData,
              isOpen: true,
              previousAmounts: ids,
            });
          }}
          reworkHandler={(rowData) => {
            if (!isSendBackForReworkVisible.isOpen) {
              const ids = getIds([rowData]);
              setIsSendBackForReworkVisible({ data: ids, isOpen: true });
            }
          }}
          onCheckboxSelect={(checkboxes) => {
            if (outputData?.length && userLabel === "L1") {
              setSelectedCheckBoxes(checkboxes);
            }
          }}
        />
      </div>
      {userLabel !== "Store" &&
        state?.type !== "All" &&
        state?.type !== "Approved" &&
        state?.type !== "Rejected" &&
        // Show buttons if there are any pending vouchers (even if some are approved)
        // For Pending/Rework types, always show buttons regardless of array lengths
        // This ensures buttons are visible even if arrays are empty but type indicates pending state
        ((state?.type === "Pending" || state?.type === "Rework") || 
         (state?.pendingDocs?.length > 0 || state?.pendingApprovedRejectedDocs?.length > 0)) && (
          <div className={`mt-4 flex items-center justify-end gap-x-5`}>
            {(() => {
              const actionsDisabledByRework = hasAnyChildInRework(state, userLabel);
              return (
                <>
            <CustomButton
              title="Submit Voucher"
              wrapperClassName={`border-[1px] !border-[var(--successGreen)] !bg-transparent self-end !text-[var(--successGreen)] !px-8 ${
                canSendAll && !actionsDisabledByRework ? "" : "disabled-kv opacity-50 cursor-not-allowed"
              }`}
              onClick={() => {
                if (actionsDisabledByRework) {
                  toast.warning(
                    "One or more sub-vouchers are in rework. Please wait for rework to be completed before submitting."
                  );
                  return;
                }
                if (canSendAll) {
                  handleSendAllToNextLevel();
                } else {
                  // Check if vouchers are in rework to provide specific message
                  const subVouchers = Array.isArray(outputData) ? outputData : [];
                  const actualSubVouchers = subVouchers.filter((sub) => !sub.subRows || sub.subRows.length === 0);
                  const vouchersInRework = actualSubVouchers.filter((sub) => checkIfVoucherInRework(sub));
                  
                  if (vouchersInRework.length > 0) {
                    toast.warning(
                      `Cannot submit: ${vouchersInRework.length} sub-voucher(s) are in rework state. ` +
                      "Please wait for rework to be completed before submitting."
                    );
                  } else {
                    toast.info("Please decide all sub-vouchers (approve/reject) before submitting.");
                  }
                }
              }}
            />
            <CustomButton
              title="Reject"
              wrapperClassName={`border-[1px] !border-[var(--blackColor)] !bg-transparent self-end !text-[var(--blackColor)] !px-8 ${
                canRejectSelectedVouchers() ? "" : "disabled-kv opacity-50 cursor-not-allowed"
              }`}
              onClick={() => {
                if (!canRejectSelectedVouchers()) {
                  const vouchersToCheck = getVouchersToProcess();
                  if (vouchersToCheck.length === 0) {
                    toast.info(userLabel === "L1" ? "Please select vouchers to reject." : "No vouchers available to reject.");
                    return;
                  }
                  const alreadyRejected = vouchersToCheck.filter((v) => isVoucherRejected(v));
                  const alreadyApproved = vouchersToCheck.filter((v) => isVoucherApproved(v));
                  const inRework = vouchersToCheck.filter((v) => checkIfVoucherInRework(v));
                  
                  if (alreadyRejected.length > 0) {
                    toast.warning(`Cannot reject: ${alreadyRejected.length} voucher(s) are already rejected.`);
                  } else if (alreadyApproved.length > 0) {
                    toast.warning(`Cannot reject: ${alreadyApproved.length} voucher(s) are already approved. Please send back for rework first if you need to change the decision.`);
                  } else if (inRework.length > 0) {
                    toast.warning(`Cannot reject: ${inRework.length} voucher(s) are in rework state. Please wait for rework to be completed first.`);
                  } else {
                    toast.info("Please select vouchers that are pending at your level.");
                  }
                  return;
                }
                
                const ids = [];
                if (userLabel === "L1") {
                  selectedCheckBoxes.forEach((row) => {
                    if (row?.original?.subRows) {
                      row?.original?.subRows?.forEach((row) => {
                        if (row?.id) {
                          ids.push(row?.id);
                          return;
                        }
                        row?.subRows?.forEach((row) => {
                          if (row?.id) {
                            ids.push(row?.id);
                            return;
                          }
                          row?.subRows?.forEach((row) => {
                            if (row?.id) {
                              ids.push(row?.id);
                              return;
                            }
                          });
                        });
                      });
                    } else {
                      ids.push(row?.original?.id);
                    }
                  });
                } else {
                  const vouchersToProcess = getVouchersToProcess();
                  vouchersToProcess.forEach((voucher) => {
                    if (voucher?.id && 
                        !isVoucherRejected(voucher) && 
                        !isVoucherApproved(voucher) && 
                        !checkIfVoucherInRework(voucher)) {
                      ids.push(voucher.id);
                    }
                  });
                }

                const validIds = [...new Set([...ids])];
                setIsRejectVisible({ data: validIds, isOpen: true });
              }}
            />
            <CustomButton
              title="Approve"
              wrapperClassName={`border-[1px] !border-[var(--blackColor)] !bg-transparent self-end !text-[var(--blackColor)] !px-8 ${
                canApproveSelectedVouchers() ? "" : "disabled-kv opacity-50 cursor-not-allowed"
              }`}
              onClick={() => {
                if (!canApproveSelectedVouchers()) {
                  const vouchersToCheck = getVouchersToProcess();
                  if (vouchersToCheck.length === 0) {
                    toast.info(userLabel === "L1" ? "Please select vouchers to approve." : "No vouchers available to approve.");
                    return;
                  }
                  const alreadyApproved = vouchersToCheck.filter((v) => isVoucherApproved(v));
                  const alreadyRejected = vouchersToCheck.filter((v) => isVoucherRejected(v));
                  const inRework = vouchersToCheck.filter((v) => checkIfVoucherInRework(v));
                  
                  if (alreadyApproved.length > 0) {
                    toast.warning(`Cannot approve: ${alreadyApproved.length} voucher(s) are already approved.`);
                  } else if (alreadyRejected.length > 0) {
                    toast.warning(`Cannot approve: ${alreadyRejected.length} voucher(s) are already rejected. Vouchers must be resubmitted before they can be approved.`);
                  } else if (inRework.length > 0) {
                    toast.warning(`Cannot approve: ${inRework.length} voucher(s) are in rework state. Please wait for rework to be completed and resubmitted first.`);
                  } else {
                    toast.info("Please select vouchers that are pending at your level.");
                  }
                  return;
                }
                
                const ids = [];
                if (userLabel === "L1") {
                  selectedCheckBoxes.forEach((row) => {
                    if (row?.original?.subRows) {
                      row?.original?.subRows?.forEach((row) => {
                        if (row?.id) {
                          ids.push({ id: row?.id, approvedAmount: row?.amount });
                          return;
                        }
                        row?.subRows?.forEach((row) => {
                          if (row?.id) {
                            ids.push({ id: row?.id, approvedAmount: row?.amount });
                            return;
                          }
                          row?.subRows?.forEach((row) => {
                            if (row?.id) {
                              ids.push({ id: row?.id, approvedAmount: row?.amount });
                              return;
                            }
                          });
                        });
                      });
                    } else {
                      ids.push({
                        id: row?.original?.id,
                        approvedAmount: approvedAmountPassAccordingToLevel(row?.original),
                      });
                    }
                  });
                } else {
                  const vouchersToProcess = getVouchersToProcess();
                  vouchersToProcess.forEach((voucher) => {
                    if (voucher?.id && 
                        !isVoucherApproved(voucher) && 
                        !isVoucherRejected(voucher) && 
                        !checkIfVoucherInRework(voucher)) {
                      ids.push({
                        id: voucher.id,
                        approvedAmount: approvedAmountPassAccordingToLevel(voucher),
                      });
                    }
                  });
                }

                const validIds = [...new Set([...ids])];
                approveRequestHandler(validIds, "", true); // saveDecisionOnly = true
              }}
            />
            
                </>
              );
            })()}
          </div>
        )}
      {isL1 ? (
        isFinance && state?.type === "Pending" ? (
          <PreviewModal
            isModalVisible={downloadPreviewOpenWithData}
            setIsModalVisible={setDownloadPreviewOpenWithData}
          />
        ) : isFinance && state?.type === "Approved" ? (
          <PreviewDocModal
            isModalVisible={downloadPreviewOpenWithData}
            setIsModalVisible={setDownloadPreviewOpenWithData}
          />
        ) : (
          <PreviewModal
            isModalVisible={downloadPreviewOpenWithData}
            setIsModalVisible={setDownloadPreviewOpenWithData}
          />
        )
      ) : (
        <PreviewDocModal
          isModalVisible={downloadPreviewOpenWithData}
          setIsModalVisible={setDownloadPreviewOpenWithData}
        />
      )}
      {isRejectVisible.isOpen && (
        <RejectModal
          isModalVisible={isRejectVisible}
          setIsModalVisible={setIsRejectVisible}
          rejectHandler={({ data = [], comment = "" }) => {
            const validIds = [...new Set([...data])];
            rejectRequestHandler(validIds, comment, true); // saveDecisionOnly = true
          }}
        />
      )}
      {isEditApproveVisible.isOpen && (
        <EditApproveModal
          isModalVisible={isEditApproveVisible}
          setIsModalVisible={setIsEditApproveVisible}
          approveHandler={({ data = [], comment = "" }) => {
            const validIds = [...new Set([...data])];
            approveRequestHandler(validIds, comment, true); // saveDecisionOnly = true
          }}
          ids={isEditApproveVisible?.previousAmounts}
          totalAmount={state?.amount || "NA"}
        />
      )}
      {isEditVoucherCommentVisible.isOpen && (
        <EditVoucherComment
          isModalVisible={isEditVoucherCommentVisible}
          setIsModalVisible={setIsEditVoucherCommentVisible}
          approveHandler={updateCommentRequestHandler}
          ids={isEditApproveVisible?.previousAmounts}
          totalAmount={state?.amount || "NA"}
        />
      )}
      {isSendBackForReworkVisible.isOpen && (
        <SendBackForReworkModal
          isModalVisible={isSendBackForReworkVisible}
          setIsModalVisible={setIsSendBackForReworkVisible}
          reworkHandler={({ data, comment = "" }) => {
            sendBackForReworkHandler(data, comment);
          }}
        />
      )}
      {isEditAfterReworkVisible.isOpen && (
        <EditAfterReworkModal
          isModalVisible={isEditAfterReworkVisible}
          setIsModalVisible={setIsEditAfterReworkVisible}
          approveHandler={editAfterReworkHandler}
          voucherData={isEditAfterReworkVisible.data}
        />
      )}
      {isReworkHistoryVisible.isOpen && (
        <ReworkHistoryModal
          isModalVisible={isReworkHistoryVisible}
          setIsModalVisible={setIsReworkHistoryVisible}
          voucherId={isReworkHistoryVisible.voucherId}
        />
      )}
    </div>
  );
};

export default VoucherDetails;
